import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Toolbox component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconToolbox extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.9994 1.88501C10.5587 1.88501 9.17698 2.45734 8.15823 3.4761C7.31784 4.31648 6.78123 5.40387 6.6191 6.56763H3.41526C2.13906 6.56763 1.10449 7.60219 1.10449 8.8784V13.5608V19.8038C1.10449 21.08 2.13906 22.1145 3.41526 22.1145H20.5837C21.8599 22.1145 22.8945 21.08 22.8945 19.8038V13.5608V8.8784C22.8945 7.60219 21.8599 6.56763 20.5837 6.56763H17.3798C17.2177 5.40387 16.6811 4.31648 15.8407 3.4761C14.8219 2.45734 13.4402 1.88501 11.9994 1.88501ZM15.8596 6.56763C15.7113 5.80374 15.3382 5.09497 14.78 4.53676C14.0426 3.79931 13.0424 3.38501 11.9994 3.38501C10.9565 3.38501 9.95634 3.79931 9.21889 4.53676C8.66067 5.09497 8.28761 5.80374 8.13925 6.56763H15.8596ZM2.60449 8.8784C2.60449 8.43062 2.96749 8.06763 3.41526 8.06763H20.5837C21.0315 8.06763 21.3945 8.43062 21.3945 8.8784V12.8108H12.7495V11.9993C12.7495 11.5851 12.4137 11.2493 11.9995 11.2493C11.5853 11.2493 11.2495 11.5851 11.2495 11.9993V12.8108H2.60449V8.8784ZM11.2495 15.1208V14.3108H2.60449V19.8038C2.60449 20.2516 2.96749 20.6145 3.41526 20.6145H20.5837C21.0315 20.6145 21.3945 20.2516 21.3945 19.8038V14.3108H12.7495V15.1208C12.7495 15.535 12.4137 15.8708 11.9995 15.8708C11.5853 15.8708 11.2495 15.535 11.2495 15.1208Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-toolbox') === undefined) {
  customElements.define('cat-icon-toolbox', BlocksIconToolbox);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-toolbox': BlocksIconToolbox;
  }
}
